import { arTranslations } from '@/core/constants/translations/ar';
import { enTranslations } from '@/core/constants/translations/en';
import { logger } from '@/core/logger';
import { getCurrentLanguage } from '@/core/utils/i18n';
import * as FileSystem from 'expo-file-system';
// @ts-ignore - expo-media-library types may not be available immediately after install
import { Buffer } from 'buffer';
import { Platform } from 'react-native';

export interface FileDownloadOptions {
    base64Content: string;
    fileName: string;
    mimeType?: string;
}

export interface FileDownloadResult {
    success: boolean;
    fileUri?: string;
    cleanup?: () => void;
    error?: string;
}

const base64ToBytes = (base64Content: string): Uint8Array =>
    Uint8Array.from(Buffer.from(base64Content, 'base64'));

const stripDataUrlPrefix = (content: string): string =>
    content.replace(/^data:.*;base64,/, '');

const guessMimeType = (fileName: string, provided?: string): string | undefined => {
    if (provided) return provided;
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'pdf':
            return 'application/pdf';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        case 'webp':
            return 'image/webp';
        default:
            return undefined;
    }
};

const buildFileUri = (dir: any, fileName: string): string =>
    new FileSystem.File(dir, fileName).uri;

let resolvedSAF: any | null | undefined;

const getSAF = async (): Promise<any | null> => {
    if (resolvedSAF !== undefined) {
        return resolvedSAF;
    }
    const fsAny = FileSystem as any;
    if (fsAny.StorageAccessFramework) {
        resolvedSAF = fsAny.StorageAccessFramework;
        return resolvedSAF;
    }
    if (fsAny.default?.StorageAccessFramework) {
        resolvedSAF = fsAny.default.StorageAccessFramework;
        return resolvedSAF;
    }
    // Fallback: try legacy module (some devices expose SAF only there)
    try {
        // @ts-ignore legacy path may not have types
        const legacy = await import('expo-file-system/legacy');
        resolvedSAF = (legacy as any).StorageAccessFramework ?? null;
        return resolvedSAF;
    } catch {
        resolvedSAF = null;
        return null;
    }
};

async function writeBase64ToUri(target: string | InstanceType<typeof FileSystem.File>, base64Content: string): Promise<void> {
    const file = typeof target === 'string' ? new FileSystem.File(target) : target;
    const stream = file.writableStream();
    const writer = stream.getWriter();
    await writer.write(base64ToBytes(base64Content));
    await writer.close();
}

/**
 * Download and save file to device
 * - Android: Saves to Downloads folder (accessible via file manager)
 * - iOS: Saves to Files app (documentDirectory)
 * - Web: Triggers browser download
 * 
 * @param options - File download options
 * @returns Promise<FileDownloadResult>
 */
export async function downloadAndSaveFile(options: FileDownloadOptions): Promise<FileDownloadResult> {
    const { base64Content, fileName } = options;
    const cleanBase64 = stripDataUrlPrefix(base64Content);
    const mimeType = guessMimeType(fileName, options.mimeType);

    try {
        if (Platform.OS === 'web') {
            // Web: Use browser download API
            const binaryString = atob(base64Content);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: mimeType || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            logger.info('FileDownload', 'File downloaded on web', { fileName, url });
            return {
                success: true,
                fileUri: url, // object URL for opening
                cleanup: () => URL.revokeObjectURL(url),
            };
        }

        // Native platforms: Save to device storage
        if (Platform.OS === 'android') {
            // Preferred: use SAF to let user pick Downloads (scoped storage compliant)
            const SAF = await getSAF();
            if (SAF?.requestDirectoryPermissionsAsync) {
                const initialDir = SAF.getUriForDirectoryInRoot
                    ? SAF.getUriForDirectoryInRoot('Download')
                    : undefined;
                try {
                    const permissions = await SAF.requestDirectoryPermissionsAsync(initialDir);

                    if (permissions.granted && permissions.directoryUri) {
                        const safFileUri = await SAF.createFileAsync(
                            permissions.directoryUri,
                            fileName,
                            mimeType || 'application/octet-stream'
                        );
                        // For SAF content URIs, use SAF write API (File streams don't support content://)
                        if (SAF.writeAsStringAsync) {
                            await SAF.writeAsStringAsync(safFileUri, cleanBase64, { encoding: 'base64' });
                        } else {
                            await writeBase64ToUri(safFileUri, cleanBase64);
                        }

                        logger.info('FileDownload', 'File saved via SAF (Android)', {
                            fileName,
                            safUri: safFileUri,
                            directoryUri: permissions.directoryUri,
                        });

                        return { success: true, fileUri: safFileUri };
                    }
                } catch (safError: any) {
                    logger.warn('FileDownload', 'SAF save failed or denied', {
                        error: safError?.message || safError,
                    });
                }
            }

            // SAF denied or unavailable; fail explicitly with user-friendly message
            if (!SAF) {
                throw new Error('Storage Access Framework not available. Please select a folder using a supported file manager.');
            }
            const language = await getCurrentLanguage();
            const copy = language === 'ar' ? arTranslations : enTranslations;
            const folderSelectionCancelled = (
                copy.propertyDetails as unknown as { folderSelectionCancelled: string }
            ).folderSelectionCancelled;
            throw new Error(folderSelectionCancelled);
        }

        if (Platform.OS === 'ios') {
            // iOS: Write directly to documentDirectory when available
            const documentDir = (FileSystem as any).documentDirectory || FileSystem.Paths?.document;
            const cacheDir = (FileSystem as any).cacheDirectory || FileSystem.Paths?.cache;
            const targetBase = documentDir ?? cacheDir;
            if (!targetBase) {
                const language = await getCurrentLanguage();
                const copy = language === 'ar' ? arTranslations : enTranslations;
                const iosNoWritableDirectory = (
                    copy.propertyDetails as unknown as { iosNoWritableDirectory: string }
                ).iosNoWritableDirectory;
                throw new Error(iosNoWritableDirectory);
            }

            // Ensure file is created via Directory API (new File API requires creation before writing)
            const directory =
                targetBase instanceof FileSystem.Directory
                    ? targetBase
                    : new FileSystem.Directory(targetBase);
            // Directory.createFile() throws if the file already exists (no
            // overwrite option) — construct the File directly and create it
            // idempotently instead, so re-downloading the same file (e.g. a
            // brochure with a fixed, non-timestamped name) doesn't crash.
            const file = new FileSystem.File(directory, fileName);
            file.create({ overwrite: true });

            // Use stream API (non-deprecated) to write base64
            await writeBase64ToUri(file, cleanBase64);

            logger.info(
                'FileDownload',
                documentDir ? 'File saved to document directory (iOS)' : 'File saved to cache (iOS)',
                {
                    fileName,
                    fileUri: file.uri,
                }
            );

            return {
                success: true,
                fileUri: file.uri,
            };
        }

        throw new Error(`Unsupported platform: ${Platform.OS}`);
    } catch (error: any) {
        logger.error('FileDownload', 'Failed to download and save file', {
            error: error?.message || error,
            fileName,
            platform: Platform.OS,
        });
        return {
            success: false,
            error: error?.message || 'Failed to save file',
        };
    }
}

